import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { BrandLogo } from "../../components/BrandLogo";
import { adminApi, money } from "../../lib/api";

const PERIODS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"]];

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!localStorage.getItem("mm_admin_token")) { navigate("/admin/login"); return; }
    setLoading(true);
    Promise.all([
      adminApi.get(`/admin/analytics?period=${period}`),
      adminApi.get(`/admin/leads`),
    ]).then(([a, l]) => { setData(a.data); setLeads(l.data); })
      .catch((e) => { if (e?.response?.status === 401) navigate("/admin/login"); })
      .finally(() => setLoading(false));
  }, [period, navigate]);

  useEffect(() => { load(); }, [load]);

  const Section = ({ title, children, tid }) => (
    <section className="mt-4 rounded-xl border bg-card p-4" data-testid={tid}>
      <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{title}</div>
      <div className="mt-3">{children}</div>
    </section>
  );

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/admin")} className="grid h-9 w-9 place-items-center rounded-full border bg-white" data-testid="analytics-back"><ChevronLeft className="h-5 w-5" /></button>
            <BrandLogo size={32} withText />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5" data-testid="admin-analytics">
        <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Analytics / Customer Journey</h1>
        <div className="mt-3 inline-flex rounded-xl border bg-white p-1" data-testid="analytics-period">
          {PERIODS.map(([val, lbl]) => (
            <button key={val} onClick={() => setPeriod(val)} data-testid={`period-${val}`}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${period === val ? "text-white" : "text-[hsl(var(--muted-foreground))]"}`}
              style={period === val ? { background: "hsl(var(--brand-rose))" } : {}}>{lbl}</button>
          ))}
        </div>

        {loading || !data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            <Section title={`Funnel · Overall conversion ${data.conversion_rate_pct}%`} tid="funnel">
              <div className="space-y-2">
                {data.funnel.map((f) => (
                  <div key={f.key} className="flex items-center gap-3">
                    <div className="w-44 shrink-0 text-sm text-[hsl(var(--muted-foreground))]">{f.label}</div>
                    <div className="h-6 flex-1 overflow-hidden rounded bg-[hsl(var(--muted))]">
                      <div className="h-full rounded" style={{ width: `${data.funnel[0].sessions ? Math.max(3, Math.round(f.sessions / data.funnel[0].sessions * 100)) : 0}%`, background: "hsl(var(--brand-rose))" }} />
                    </div>
                    <div className="w-28 shrink-0 text-right text-sm tabular-nums">{f.sessions}{f.from_prev_pct != null && <span className="ml-1 text-[11px] text-[hsl(var(--muted-foreground))]">({f.from_prev_pct}%)</span>}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="CTA performance (by source)" tid="cta">
              {data.cta.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No CTA clicks yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-[hsl(var(--muted-foreground))]"><th className="pb-1">Source</th><th className="pb-1 text-right">Clicks</th><th className="pb-1 text-right">Sessions</th></tr></thead>
                  <tbody>{data.cta.map((c) => (<tr key={c.source || "none"} className="border-t"><td className="py-1.5 font-medium">{c.source || "(unknown)"}</td><td className="py-1.5 text-right tabular-nums">{c.clicks}</td><td className="py-1.5 text-right tabular-nums">{c.sessions}</td></tr>))}</tbody>
                </table>
              )}
            </Section>

            <Section title="Quantities selected" tid="quantities">
              <div className="flex flex-wrap gap-2">
                {data.quantities.map((q) => (<span key={q.qty} className="rounded-lg border px-2.5 py-1 text-sm">{q.qty} magnets · <b>{q.count}</b></span>))}
                <span className="rounded-lg border border-dashed px-2.5 py-1 text-sm">Custom · <b>{data.custom_quantity.count}</b>{data.custom_quantity.count ? ` (avg ${data.custom_quantity.avg_qty})` : ""}</span>
                {data.quantities.length === 0 && data.custom_quantity.count === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No orders in this period.</p>}
              </div>
            </Section>

            <Section title="Add-ons" tid="addons">
              {data.add_ons.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No add-on orders yet.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-[hsl(var(--muted-foreground))]"><th className="pb-1">Add-on</th><th className="pb-1 text-right">Reached</th><th className="pb-1 text-right">Selected</th><th className="pb-1 text-right">Attach</th><th className="pb-1 text-right">Revenue</th></tr></thead>
                  <tbody>{data.add_ons.map((a) => (<tr key={a.id} className="border-t"><td className="py-1.5 font-medium">{a.title}</td><td className="py-1.5 text-right tabular-nums">{a.reached}</td><td className="py-1.5 text-right tabular-nums">{a.selected}</td><td className="py-1.5 text-right tabular-nums">{a.attach_rate_pct}%</td><td className="py-1.5 text-right tabular-nums">{money(a.revenue_cents)}</td></tr>))}</tbody>
                </table>
              )}
            </Section>

            <Section title="Checkout abandonment (open leads by last step)" tid="abandonment">
              {data.abandonment.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No abandoned checkouts in this period.</p> : (
                <div className="flex flex-wrap gap-2">{data.abandonment.map((a) => (<span key={a.step} className="rounded-lg border px-2.5 py-1 text-sm">{a.step} · <b>{a.count}</b></span>))}</div>
              )}
            </Section>

            <Section title={`Abandoned checkout leads · ${leads?.counts?.OPEN || 0} open / ${leads?.counts?.RECOVERED || 0} recovered`} tid="leads">
              {!leads?.leads?.length ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No leads yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-[hsl(var(--muted-foreground))]"><th className="pb-1">Name</th><th className="pb-1">Email</th><th className="pb-1 text-right">Qty</th><th className="pb-1">Last step</th><th className="pb-1">Status</th></tr></thead>
                    <tbody>{leads.leads.map((l) => (
                      <tr key={l.id} className="border-t" data-testid="lead-row">
                        <td className="py-1.5">{l.name || "—"}</td><td className="py-1.5">{l.email}</td>
                        <td className="py-1.5 text-right tabular-nums">{l.kit_qty || "—"}</td>
                        <td className="py-1.5 text-xs">{l.last_step}</td>
                        <td className="py-1.5"><span className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-white" style={{ background: l.status === "RECOVERED" ? "hsl(var(--success))" : l.status === "EXPIRED" ? "hsl(var(--muted-foreground))" : "hsl(var(--warning))" }}>{l.status}</span></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
